/**
 * Envelope builder for asset-register events.
 *
 * Produces the `POST /v2/events` body: the generic envelope plus `detail.ledger` plus the one
 * type-specific branch. Pure - no clock, no network, no store - so the same builder serves the
 * online path, an offline queue and the tests.
 *
 * SCAFFOLD: the branch payload is passed through unvalidated. Per-type branch validation
 * (`devdocs/features/asset-register/design/events-schema.md` §7.3) is not built yet.
 */

import { assetEventTypeDef, isAssetEventType } from './asset-event-types'
import { buildEventLedger, resolveEventTime } from './ledger'

const asIdList = value => {
    if (value == null) return []
    const list = Array.isArray(value) ? value : [value]
    return [
        ...new Set(
            list
                .filter(id => id != null && id !== '')
                .map(Number)
                .filter(Number.isFinite),
        ),
    ]
}

/**
 * The envelope's `time` is a timestamp, not a date.
 *
 * Every writer sources it from a date field a person picked - `disposedDate`, `acquiredDate`,
 * `improve.date`, `effectiveFrom` - and a `<input type="date">` hands back `YYYY-MM-DD`. The API
 * parses `time` as a time.Time and answers `400 {"error":"invalid_json"}` for a bare date, with no
 * indication of which field it disliked. Every asset event written before this normalisation was
 * rejected, and the register had no reader to notice the ledger was empty.
 *
 * Since 13 Oct 2026 a picked date is a real UTC instant, in the browser's timezone (the backend is
 * UTC throughout): today's date is the moment of recording, any other date is local noon. The day as
 * picked lives in `detail.ledger.localDate`; read it with `eventDay`. See `resolveEventTime`.
 */

export const normalizeEventTime = (time, recordedAt) => (typeof time === 'string' && time ? resolveEventTime(time, recordedAt).time : time)

/**
 * @param {{
 *   type: string,
 *   assetId: number,
 *   farmIds: number[]|number,
 *   time: string,
 *   recordedAt: string,
 *   source: string,
 *   detail?: Object,
 *   subtype?: string,
 *   actor?: Object,
 *   paddockIds?: number[]|number,
 *   userIds?: number[]|number,
 *   assetIds?: number[],
 *   notes?: string,
 *   ledger?: Object,
 * }} input
 * @returns {Object} event body
 */
export const buildAssetEvent = ({ type, assetId, assetIds, farmIds, time, recordedAt, source, detail = {}, subtype, actor, paddockIds, userIds, notes, ledger: ledgerExtras = {} } = {}) => {
    const def = assetEventTypeDef(type)
    if (!def) throw new Error(`buildAssetEvent: "${type}" is not an asset-register event type`)
    if (def.subtypes && subtype && !def.subtypes.includes(subtype)) {
        throw new Error(`buildAssetEvent: "${subtype}" is not a valid subtype for ${type} (${def.subtypes.join(', ')})`)
    }

    // assetIds[0] is the primary subject by convention; extra ids follow when an event touches more
    // than one row (a transfer, a containment).
    const subjects = asIdList([assetId, ...(assetIds || [])])
    if (!subjects.length) throw new Error('buildAssetEvent: assetId is required - assetIds[0] is the primary subject')

    const farms = asIdList(farmIds)
    if (!farms.length) throw new Error('buildAssetEvent: farmIds is required')
    if (!time) throw new Error('buildAssetEvent: time is required')
    // A picked date is stored as a real UTC instant (see `resolveEventTime`); the ledger keeps the picked
    // day as `localDate`, and `backdated` compares days rather than instants for it.
    const resolved = resolveEventTime(time, recordedAt)
    const eventTime = resolved.time
    const ledger = buildEventLedger({ time: eventTime, recordedAt, source, actor, ...(resolved.localDate ? { timePrecision: resolved.timePrecision, localDate: resolved.localDate } : {}), ...ledgerExtras })
    const paddocks = asIdList(paddockIds ?? ledger.paddockId)
    const users = asIdList(userIds ?? (ledger.actor?.kind === 'user' ? ledger.actor.userId : null))

    return {
        type,
        time: eventTime,
        farmIds: farms,
        assetIds: subjects,
        ...(paddocks.length ? { paddockIds: paddocks } : {}),
        // Empty userIds must be omitted, never sent as [null] - the API answers reference_not_found.
        ...(users.length ? { userIds: users } : {}),
        detail: {
            ...(subtype ? { subtype } : {}),
            [def.branch]: detail,
            ...(notes ? { notes } : {}),
            ledger,
        },
    }
}

export default buildAssetEvent
